import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import {
  appIncidentsQuery,
  saveAppIncidentSettings,
  type AppIncident,
  type AppIncidentSetting,
  type AppIncidentsPage,
} from '../api/appIncidents'
import { toast } from '../ui/notify'
import { failed } from './parts'
import './AppIncidents.css'

/**
 * "Open incidents" pe fisa aplicatiei: cand doua sau mai multe componente au
 * alarme in aceeasi fereastra, Easeedesk deschide UN tichet al aplicatiei
 * ("Charisma ERP unavailable / degraded") cu tichetele alarmelor dedesubt.
 * Aici se vad incidentele, cu componentele si tichetele lor.
 *
 * Sub lista, bifa: STINSA implicit. Pe aplicatie (On / Off / ca furnizorul)
 * si, pentru administrator, pe furnizor - toate aplicatiile. Se leaga numai
 * alarmele nascute dupa aprindere.
 *
 * Fara drept sau inainte de migrare, cardul nu apare.
 */
export function AppIncidents({ appId }: { appId: number }) {
  const [all, setAll] = useState(false)
  const [editing, setEditing] = useState(false)
  const page = useQuery({ ...appIncidentsQuery(appId, all), retry: false })
  const data = page.data
  if (!data?.ready || !Array.isArray(data.incidents)) return null
  const list = data.incidents

  return (
    <>
      <div className="adm-section">
        <h4>Open incidents</h4>
        <span className="adm-section-note">alarms on several components, grouped into one ticket</span>
        <label className="adm-muted small" style={{ marginLeft: 'auto', fontWeight: 'normal' }}>
          <input type="checkbox" id="appIncidentsAll" checked={all} onChange={(ev) => setAll(ev.target.checked)} /> Show closed
        </label>
      </div>
      <div className="adm-card" id="appIncidents">
        {list.length === 0 ? (
          <p className="text-muted" style={{ padding: '10px 15px' }}>
            {!data.enabled
              ? 'Grouping is off: alarms on this application stay separate tickets.'
              : all
                ? 'No incidents yet.'
                : 'No open incidents.'}
          </p>
        ) : (
          list.map((i) => <IncidentBlock key={i.id} incident={i} />)
        )}
        <div className="ai-settings">
          <span className="adm-muted small" id="appIncidentsState">
            {data.enabled
              ? `Grouping on: alarms within ${data.windowMinutes ?? 30} minutes become one incident${data.since ? ` (alarms since ${data.since})` : ''}.`
              : 'Grouping off.'}
            {data.appSetting === 'inherit' ? ' Follows the provider default.' : ' Set for this application.'}
          </span>
          {data.canEdit && !editing && (
            <button type="button" id="appIncidentsEdit" className="btn btn-default btn-xs" onClick={() => setEditing(true)}>
              <i className="fa fa-cog"></i> Grouping
            </button>
          )}
        </div>
        {editing && <SettingsForm appId={appId} data={data} onDone={() => setEditing(false)} />}
      </div>
    </>
  )
}

function IncidentBlock({ incident }: { incident: AppIncident }) {
  const tone = incident.state === 'unavailable' ? 'label-danger' : 'label-warning'
  return (
    <div className={`ai-incident${incident.open ? '' : ' ai-closed'}`}>
      <a href={`/app/easeedesk/ticket/${incident.ticketId}`} target="_blank" rel="noopener noreferrer">
        #{incident.ticketId} {incident.subject}
      </a>
      <span className={`label ${tone} ai-state`}>{incident.state}</span>
      <span className="adm-muted small">
        {' '}
        since {incident.openedAt}
        {incident.closedAt ? `, closed ${incident.closedAt}` : ''}
      </span>
      <ul>
        {incident.tickets.map((t) => (
          <li key={t.id}>
            {t.component && <strong>{t.component}: </strong>}
            <a href={`/app/easeedesk/ticket/${t.id}`} target="_blank" rel="noopener noreferrer">
              #{t.id} {t.subject}
            </a>{' '}
            <span className="adm-muted small">{t.closed ? 'closed' : t.status}</span>
          </li>
        ))}
      </ul>
    </div>
  )
}

function SettingsForm({ appId, data, onDone }: { appId: number; data: AppIncidentsPage; onDone: () => void }) {
  const qc = useQueryClient()
  const [mine, setMine] = useState<AppIncidentSetting>(data.appSetting ?? 'inherit')
  const [provider, setProvider] = useState<'on' | 'off'>(data.providerEnabled ? 'on' : 'off')
  const [windowMinutes, setWindowMinutes] = useState(String(data.windowMinutes ?? 30))
  const [busy, setBusy] = useState(false)

  const save = async () => {
    setBusy(true)
    try {
      const minutes = Number(windowMinutes) || 30
      if (data.canEditProvider && provider !== (data.providerEnabled ? 'on' : 'off')) {
        const r = await saveAppIncidentSettings(appId, { scope: 'provider', enabled: provider, window: minutes })
        if (r.status !== 'OK') throw new Error(r.message ?? 'Not saved.')
      }
      const r = await saveAppIncidentSettings(appId, { scope: 'app', enabled: mine, window: minutes })
      if (r.status !== 'OK') throw new Error(r.message ?? 'Not saved.')
      toast('Saved.', 'Open incidents', 'success')
      await qc.invalidateQueries({ queryKey: ['business-apps', 'incidents'] })
      onDone()
    } catch (e) {
      toast(failed(e), 'Open incidents', 'error')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="ai-settings" id="appIncidentsSettings">
      <label className="small">
        This application{' '}
        <select className="form-control input-sm" id="appIncidentsMine" value={mine} onChange={(e) => setMine(e.target.value as AppIncidentSetting)}>
          <option value="inherit">Provider default ({data.providerEnabled ? 'on' : 'off'})</option>
          <option value="on">On</option>
          <option value="off">Off</option>
        </select>
      </label>
      {data.canEditProvider && (
        <label className="small">
          All applications{' '}
          <select
            className="form-control input-sm"
            id="appIncidentsProvider"
            value={provider}
            onChange={(e) => setProvider(e.target.value === 'on' ? 'on' : 'off')}
          >
            <option value="off">Off</option>
            <option value="on">On</option>
          </select>
        </label>
      )}
      <label className="small">
        Window{' '}
        <input
          type="number"
          min={5}
          max={1440}
          className="form-control input-sm"
          id="appIncidentsWindow"
          value={windowMinutes}
          onChange={(e) => setWindowMinutes(e.target.value)}
        />{' '}
        min
      </label>
      <button type="button" className="btn btn-primary btn-xs" id="appIncidentsSave" disabled={busy} onClick={save}>
        Save
      </button>
      <button type="button" className="btn btn-default btn-xs" onClick={onDone}>
        Cancel
      </button>
    </div>
  )
}
